import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaFileInvoiceDollar,
  FaExchangeAlt,
  FaReceipt,
  FaMoneyBillWave,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import InvoiceService from '@frontend/AppSettings/services/InvoiceService';
import FinancialService from '@frontend/AppSettings/services/FinancialService';
import GenExpensesService from '@frontend/AppSettings/services/GenExpensesService';
import OppExpService from '@frontend/AppSettings/services/OppExpService';
import VenExpService from '@frontend/AppSettings/services/VenExpService';
import FixBillService from '@frontend/AppSettings/services/FixBillService';
import ClientMattersService from '@frontend/AppSettings/services/ClientMattersService';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import OrganizationService from '@frontend/AppSettings/services/OrganizationService';
import { useOrganizationAnchor } from '../../OrganizationAnchorContext';
import { isLegalEnvironment } from '@frontend/AppSettings/environmentConfig';
import Table from '@frontend/Components/Table/Table';
import FilterDropdown from '@frontend/Components/FilterDropdown/FilterDropdown';
import type { Column } from '@frontend/Models/Table/TableModels';
import './StakeholderFinancials.css';

interface StakeholderFinancialsProps {
  stakeholderId: string;
  type: 'individual' | 'organization';
}

const StakeholderFinancials: React.FC<StakeholderFinancialsProps> = ({ stakeholderId, type }) => {
  // The REAL org (P2 Build J), when this detail view resolved one — Build M's summary read
  // replaces the fan-out below with one round trip for exactly these.
  const orgAnchor = useOrganizationAnchor();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [activeTable, setActiveTable] = useState<'invoices' | 'transactions' | 'bills' | 'expenses'>('invoices');
  const [matterFilter, setMatterFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [loading, setLoading] = useState({
    invoices: false,
    transactions: false,
    bills: false,
    expenses: false,
  });
  const [matterIds, setMatterIds] = useState<string[]>([]);

  const entityLabel = isLegalEnvironment ? 'Matter' : 'Project';
  const entityDetailsPath = isLegalEnvironment ? '/matterDetails' : '/projectDetails';

  const fetchMatterIds = useCallback(async () => {
    try {
      if (!stakeholderId) return [];
      
      let allMatters: any[] = [];

      if (type === 'organization' && orgAnchor) {
        // A REAL org (P2 Build M): the summary read returns the org's matter ids in ONE
        // round trip — retiring the getClients + getAgents + N× getClientMatters fan-out
        // below (landmine L12). Server-gated {1,2}; a caller below that wall (or a
        // pre-migration backend) falls back to the legacy path — prefer-new,
        // fall-back-to-old, the 0008 resolver shape.
        try {
          const summary = await OrganizationService.getSummary(orgAnchor.organization.id);
          const ids = (summary.matterids || []).filter(Boolean);
          setMatterIds(ids);
          return ids;
        } catch (error) {
          console.error('Org summary unavailable, using legacy matter fan-out:', error);
        }
      }

      if (type === 'organization') {
        // For organizations, extract the actual ID and find all clients/agents with the same company
        const actualId = stakeholderId.startsWith('org-')
          ? stakeholderId.replace('org-', '')
          : stakeholderId;
        
        const clients = await ClientService.getClients();
        const agents = await AgentsService.getAgents();
        
        // Find the organization's company name
        let companyName: string | null = null;
        let foundClient = clients.find((c: any) => String(c.ClientID) === actualId);
        if (foundClient && foundClient.Company) {
          companyName = foundClient.Company;
        } else {
          let foundAgent = agents.find((a: any) => String(a.AgentID) === actualId);
          if (foundAgent && foundAgent.Company) {
            companyName = foundAgent.Company;
          }
        }

        if (companyName) {
          // Get all client IDs with this company name (as strings for API)
          const orgClientIds = clients
            .filter((c: any) => c.Company === companyName)
            .map((c: any) => String(c.ClientID));
          
          // Fetch matters for all clients in the organization
          const mattersPromises = orgClientIds.map(async (clientId: string) => {
            try {
              return await ClientMattersService.getClientMatters(clientId);
            } catch (error) {
              console.error(`Error fetching matters for client ${clientId}:`, error);
              return [];
            }
          });
          
          const mattersArrays = await Promise.all(mattersPromises);
          allMatters = mattersArrays.flat();
        }
      } else {
        // For individuals, use the stakeholderId directly
        const responseData = await ClientMattersService.getClientMatters(stakeholderId);
        allMatters = Array.isArray(responseData) ? responseData : [];
      }

      // Ensure allMatters is an array before processing
      if (!Array.isArray(allMatters)) {
        allMatters = [];
      }

      // Remove duplicates and extract matter IDs
      const uniqueMatters = Array.from(
        new Map(allMatters.map((m: any) => [m.matterid, m])).values()
      );
      
      const ids = uniqueMatters.map((m: any) => m.matterid).filter(Boolean);
      setMatterIds(ids);
      return ids;
    } catch (error) {
      console.error('Error fetching matter IDs:', error);
      setMatterIds([]);
      return [];
    }
  }, [stakeholderId, type, orgAnchor]);

  const fetchInvoices = useCallback(async () => {
    setLoading((prev) => ({ ...prev, invoices: true }));
    try {
      if (!stakeholderId) {
        setInvoices([]);
        return;
      }
      const list = await InvoiceService.getInvoicesByMatterId(stakeholderId);
      const filtered = (Array.isArray(list) ? list : []).filter(Boolean);
      const sorted = filtered.sort(
        (a, b) =>
          new Date(b.created_at || b.date || 0).getTime() -
          new Date(a.created_at || a.date || 0).getTime()
      );
      setInvoices(sorted);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setLoading((prev) => ({ ...prev, invoices: false }));
    }
  }, [stakeholderId]);

  const fetchTransactions = useCallback(async (ids: string[]) => {
    setLoading((prev) => ({ ...prev, transactions: true }));
    try {
      if (ids.length === 0) {
        setTransactions([]);
        return;
      }
      const [invLists, venLists, oppLists, genLists, fixLists] = await Promise.all([
        Promise.all(
          ids.map((id) => InvoiceService.getInvoicesByMatterId(id).catch(() => []))
        ),
        Promise.all(
          ids.map((id) => VenExpService.getVenExpensesByMatter(id).catch(() => []))
        ),
        Promise.all(
          ids.map((id) => OppExpService.getOppExpensesByMatter(id).catch(() => []))
        ),
        Promise.all(
          ids.map((id) => GenExpensesService.getGenExpensesByMatter(id).catch(() => []))
        ),
        Promise.all(
          ids.map((id) => FixBillService.getFixBillExpensesByMatter(id).catch(() => []))
        ),
      ]);

      const allTransactions = [
        ...invLists.flat(),
        ...venLists.flat(),
        ...oppLists.flat(),
        ...genLists.flat(),
        ...fixLists.flat(),
      ]
        .filter(Boolean)
        .map((x: any) => ({
          id: x.invoiceid || x.ve_id || x.oe_id || x.ge_id || x.fb_id || x.id,
          type: x.invoiceid ? 'Invoice' : x.ve_id ? 'Vendor Expense' : x.oe_id ? 'Opponent Expense' : x.ge_id ? 'General Expense' : 'Fixed Bill',
          related: x.matterid || '',
          date: x.created_at || x.date || x.date_recorded || x.date_added,
          amount: typeof x.total_amount === 'number' ? x.total_amount : typeof x.amount === 'number' ? x.amount : typeof x.fees === 'number' ? x.fees : parseFloat(x.amount || x.fees || x.total_amount || 0),
        }))
        .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());

      setTransactions(allTransactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoading((prev) => ({ ...prev, transactions: false }));
    }
  }, []);

  const fetchBills = useCallback(async () => {
    setLoading((prev) => ({ ...prev, bills: true }));
    try {
      if (!stakeholderId) {
        setBills([]);
        return;
      }

      let billedTime: any[] = [];
      let fixedFees: any[] = [];
      let otherBilling: any[] = [];

      [billedTime, fixedFees] = await Promise.all([
        FinancialService.getBilledTimeByClientId(stakeholderId).catch(() => []),
        FinancialService.getAssignedFixedFeesByClientId(stakeholderId).catch(() => []),
      ]);

      const billedMapped = (Array.isArray(billedTime) ? billedTime : [])
        .filter(Boolean)
        .map((x: any) => ({
          id: x.bt_id || x.id,
          matterid: x.matterid,
          date: x.date_recorded || x.date || x.created_at,
          amount: typeof x.amount === 'number' ? x.amount : parseFloat(x.amount) || 0,
          category: 'Billed Time',
          description: x.notes || '',
        }));

      const fixedFeesMapped = (Array.isArray(fixedFees) ? fixedFees : [])
        .filter(Boolean)
        .map((x: any) => ({
          id: x.assignedid || x.id,
          matterid: x.matterid,
          date: x.date_added || x.date || x.created_at,
          amount: typeof x.fees === 'number' ? x.fees : parseFloat(x.fees) || 0,
          category: 'Fixed Fee',
          description: x.narrative || '',
        }));

      if (otherBilling.length === 0) {
        try {
          const data = await ClientMattersService.getClientMatters(stakeholderId);
          const ids = (data || []).map((m: any) => m.matterid).filter(Boolean);
          if (ids.length > 0) {
            const lists = await Promise.all(
              ids.map((id: string) =>
                FinancialService.getOtherBillingByMatter(id).catch(() => [])
              )
            );
            otherBilling = lists.flat();
          }
        } catch (_) {}
      }

      const otherMapped = (Array.isArray(otherBilling) ? otherBilling : [])
        .filter(Boolean)
        .map((x: any) => ({
          id: x.ob_id || x.id,
          matterid: x.matterid,
          date: x.date || x.created_at,
          amount: typeof x.amount === 'number' ? x.amount : parseFloat(x.amount) || 0,
          category: 'Other Billing',
          description: x.description || '',
        }));

      const merged = [...billedMapped, ...fixedFeesMapped, ...otherMapped].sort(
        (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
      );
      setBills(merged);
    } catch (error) {
      console.error('Error fetching bills:', error);
      setBills([]);
    } finally {
      setLoading((prev) => ({ ...prev, bills: false }));
    }
  }, [stakeholderId]);

  const fetchExpenses = useCallback(async (ids: string[]) => {
    setLoading((prev) => ({ ...prev, expenses: true }));
    try {
      if (ids.length === 0) {
        setExpenses([]);
        return;
      }
      const [venLists, oppLists, genLists] = await Promise.all([
        Promise.all(
          ids.map((id) => VenExpService.getVenExpensesByMatter(id).catch(() => []))
        ),
        Promise.all(
          ids.map((id) => OppExpService.getOppExpensesByMatter(id).catch(() => []))
        ),
        Promise.all(
          ids.map((id) => GenExpensesService.getGenExpensesByMatter(id).catch(() => []))
        ),
      ]);
      const flat = [...venLists.flat(), ...oppLists.flat(), ...genLists.flat()]
        .filter(Boolean)
        .sort(
          (a: any, b: any) =>
            new Date(b.date || b.created_at || 0).getTime() -
            new Date(a.date || a.created_at || 0).getTime()
        );
      setExpenses(flat);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setExpenses([]);
    } finally {
      setLoading((prev) => ({ ...prev, expenses: false }));
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      const ids = await fetchMatterIds();
      if (!mounted) return;
      await Promise.all([
        fetchInvoices(),
        fetchTransactions(ids),
        fetchBills(),
        fetchExpenses(ids),
      ]);
    };
    run();
    return () => {
      mounted = false;
    };
  }, [fetchMatterIds, fetchInvoices, fetchTransactions, fetchBills, fetchExpenses]);

  const matchesDateFilter = useCallback((rawDate: string | undefined, filter: string) => {
    if (filter === 'all') return true;
    if (!rawDate) return false;
    const parsed = Date.parse(rawDate);
    if (Number.isNaN(parsed)) return false;

    const date = new Date(parsed);
    date.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (filter) {
      case 'today':
        return date.getTime() === today.getTime();
      case 'week': {
        const end = new Date(today);
        end.setDate(today.getDate() + 7);
        return date.getTime() >= today.getTime() && date.getTime() <= end.getTime();
      }
      case 'month': {
        const end = new Date(today);
        end.setMonth(today.getMonth() + 1);
        return date.getTime() >= today.getTime() && date.getTime() <= end.getTime();
      }
      case 'year': {
        const end = new Date(today);
        end.setFullYear(today.getFullYear() + 1);
        return date.getTime() >= today.getTime() && date.getTime() <= end.getTime();
      }
      case 'overdue':
        return date.getTime() < today.getTime();
      default:
        return true;
    }
  }, []);

  const typeOptions = useMemo(() => {
    const invoiceTypes = new Set<string>();
    const transactionTypes = new Set<string>();
    const billTypes = new Set<string>();
    const expenseTypes = new Set<string>();

    invoices.forEach(() => invoiceTypes.add('Invoice'));
    transactions.forEach((t) => t.type && transactionTypes.add(t.type));
    bills.forEach((b) => b.category && billTypes.add(b.category));
    expenses.forEach((e) => e.type && expenseTypes.add(e.type));

    const toOptions = (set: Set<string>) =>
      Array.from(set)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((value) => ({ value, label: value }));

    return {
      invoices: toOptions(invoiceTypes),
      transactions: toOptions(transactionTypes),
      bills: toOptions(billTypes),
      expenses: toOptions(expenseTypes),
    };
  }, [invoices, transactions, bills, expenses]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      if (matterFilter !== 'all' && inv.matterid !== matterFilter) return false;
      if (typeFilter !== 'all' && typeFilter !== 'Invoice') return false;
      if (dateFilter !== 'all' && !matchesDateFilter(inv.created_at || inv.date, dateFilter)) return false;
      return true;
    });
  }, [invoices, matterFilter, typeFilter, dateFilter, matchesDateFilter]);

  const filteredTransactions = useMemo(() => {
    return transactions.filter((t) => {
      if (matterFilter !== 'all' && t.related !== matterFilter) return false;
      if (typeFilter !== 'all' && t.type !== typeFilter) return false;
      if (dateFilter !== 'all' && !matchesDateFilter(t.date, dateFilter)) return false;
      return true;
    });
  }, [transactions, matterFilter, typeFilter, dateFilter, matchesDateFilter]);

  const filteredBills = useMemo(() => {
    return bills.filter((b) => {
      if (matterFilter !== 'all' && b.matterid !== matterFilter) return false;
      if (typeFilter !== 'all' && b.category !== typeFilter) return false;
      if (dateFilter !== 'all' && !matchesDateFilter(b.date || b.created_at, dateFilter)) return false;
      return true;
    });
  }, [bills, matterFilter, typeFilter, dateFilter, matchesDateFilter]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter((e) => {
      if (matterFilter !== 'all' && e.matterid !== matterFilter) return false;
      if (typeFilter !== 'all' && e.type !== typeFilter) return false;
      if (dateFilter !== 'all' && !matchesDateFilter(e.date || e.created_at, dateFilter)) return false;
      return true;
    });
  }, [expenses, matterFilter, typeFilter, dateFilter, matchesDateFilter]);

  const summary = useMemo(() => {
    return {
      invoiceCount: filteredInvoices.length,
      transactionCount: filteredTransactions.length,
      billsCount: filteredBills.length,
      expensesCount: filteredExpenses.length,
    };
  }, [filteredInvoices, filteredTransactions, filteredBills, filteredExpenses]);

  const invoiceColumns: Column<any>[] = [
    {
      header: 'Invoice',
      render: (inv: any) => (
        <Link
          to={`/invoice/${inv.invoiceid}`}
          className="stakeholder-financial-link"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          {inv.invoice_number ?? inv.invoiceid}
          <FaExternalLinkAlt style={{ fontSize: '0.8rem', opacity: 0.7 }} />
        </Link>
      ),
    },
    {
      header: entityLabel,
      accessor: 'matterid',
    },
    {
      header: 'Created',
      render: (inv: any) =>
        inv.created_at ? new Date(inv.created_at).toLocaleDateString() : 'N/A',
    },
    {
      header: 'Due Date',
      render: (inv: any) =>
        inv.due_date ? new Date(inv.due_date).toLocaleDateString() : 'N/A',
    },
    {
      header: 'Total',
      render: (inv: any) =>
        typeof inv.total_amount === 'number'
          ? `$${inv.total_amount.toFixed(2)}`
          : 'N/A',
    },
  ];

  const transactionsColumns: Column<any>[] = [
    { header: 'Transaction ID', accessor: 'id' },
    { header: 'Type', accessor: 'type' },
    {
      header: entityLabel,
      accessor: 'related',
    },
    {
      header: 'Date',
      render: (t: any) => (t.date ? new Date(t.date).toLocaleDateString() : 'N/A'),
    },
    {
      header: 'Amount',
      render: (t: any) =>
        typeof t.amount === 'number' ? `$${t.amount.toFixed(2)}` : 'N/A',
    },
  ];

  const billsColumns: Column<any>[] = [
    { header: 'Bill ID', accessor: 'id' },
    {
      header: entityLabel,
      accessor: 'matterid',
    },
    {
      header: 'Date',
      render: (r: any) =>
        r.date
          ? new Date(r.date).toLocaleDateString()
          : r.created_at
          ? new Date(r.created_at).toLocaleDateString()
          : 'N/A',
    },
    {
      header: 'Amount',
      render: (r: any) =>
        typeof r.amount === 'number'
          ? `$${r.amount.toFixed(2)}`
          : r.amount
          ? `$${Number(r.amount).toFixed(2)}`
          : 'N/A',
    },
    { header: 'Category', accessor: 'category' },
  ];

  const expensesColumns: Column<any>[] = [
    { header: 'Expense ID', accessor: 'id' },
    { header: 'Type', accessor: 'type' },
    {
      header: entityLabel,
      accessor: 'matterid',
    },
    {
      header: 'Date',
      render: (r: any) =>
        r.date
          ? new Date(r.date).toLocaleDateString()
          : r.created_at
          ? new Date(r.created_at).toLocaleDateString()
          : 'N/A',
    },
    {
      header: 'Amount',
      render: (r: any) =>
        typeof r.amount === 'number'
          ? `$${r.amount.toFixed(2)}`
          : r.amount
          ? `$${Number(r.amount).toFixed(2)}`
          : 'N/A',
    },
  ];

  const matterOptions = useMemo(
    () =>
      matterIds
        .slice()
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((id) => ({ value: id, label: id })),
    [matterIds]
  );

  const tableConfigs = useMemo(
    () => ({
      invoices: {
        title: 'Invoices',
        loading: loading.invoices,
        data: filteredInvoices,
        columns: invoiceColumns,
        rowKey: 'invoiceid',
        empty: `No invoices found for this ${type === 'organization' ? 'organization' : 'individual'}`,
      },
      transactions: {
        title: 'Transactions',
        loading: loading.transactions,
        data: filteredTransactions,
        columns: transactionsColumns,
        rowKey: 'id',
        empty: `No transactions found for this ${type === 'organization' ? 'organization' : 'individual'}`,
      },
      bills: {
        title: 'Bills',
        loading: loading.bills,
        data: filteredBills,
        columns: billsColumns,
        rowKey: 'id',
        empty: `No bills found for this ${type === 'organization' ? 'organization' : 'individual'}`,
      },
      expenses: {
        title: 'Expenses',
        loading: loading.expenses,
        data: filteredExpenses,
        columns: expensesColumns,
        rowKey: 'id',
        empty: `No expenses found for this ${type === 'organization' ? 'organization' : 'individual'}`,
      },
    }),
    [
      billsColumns,
      expensesColumns,
      filteredBills,
      filteredExpenses,
      filteredInvoices,
      filteredTransactions,
      invoiceColumns,
      loading.bills,
      loading.expenses,
      loading.invoices,
      loading.transactions,
      transactionsColumns,
      type,
    ]
  );

  const activeConfig = tableConfigs[activeTable];

  return (
    <div className="StakeholderFinancials">
      <div className="stakeholder-section-header">

      </div>

      <div className="stakeholder-summary-grid">
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">Invoices</div>
            <div className="stakeholder-summary-icon icon-muted">
              <FaFileInvoiceDollar />
            </div>
          </div>
          <div className="stakeholder-summary-value">{summary.invoiceCount}</div>
        </div>
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">Transactions</div>
            <div className="stakeholder-summary-icon icon-blue">
              <FaExchangeAlt />
            </div>
          </div>
          <div className="stakeholder-summary-value summary-blue">{summary.transactionCount}</div>
        </div>
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">Bills</div>
            <div className="stakeholder-summary-icon icon-red">
              <FaReceipt />
            </div>
          </div>
          <div className="stakeholder-summary-value summary-red">{summary.billsCount}</div>
        </div>
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">Expenses</div>
            <div className="stakeholder-summary-icon icon-green">
              <FaMoneyBillWave />
            </div>
          </div>
          <div className="stakeholder-summary-value summary-green">{summary.expensesCount}</div>
        </div>
      </div>

      <div className="stakeholder-financials-toggle">
        {[
          { id: 'invoices', label: 'Invoices' },
          { id: 'transactions', label: 'Transactions' },
          { id: 'bills', label: 'Bills' },
          { id: 'expenses', label: 'Expenses' },
        ].map((item) => (
          <button
            key={item.id}
            className={`financial-toggle-btn ${activeTable === item.id ? 'active' : ''}`}
            onClick={() => setActiveTable(item.id as typeof activeTable)}
            type="button"
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="stakeholder-financial-filters-row">
        <FilterDropdown
          label=""
          placeholder={isLegalEnvironment ? 'All Matters' : 'All Projects'}
          options={[
            { value: 'all', label: isLegalEnvironment ? 'All Matters' : 'All Projects' },
            ...matterOptions,
          ]}
          value={matterFilter}
          onChange={(value) => setMatterFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-financial-filter-dropdown"
        />
        <FilterDropdown
          label=""
          placeholder="All Types"
          options={[{ value: 'all', label: 'All Types' }, ...(typeOptions[activeTable] || [])]}
          value={typeFilter}
          onChange={(value) => setTypeFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-financial-filter-dropdown"
        />
        <FilterDropdown
          label=""
          placeholder="All Dates"
          options={[
            { value: 'all', label: 'All Dates' },
            { value: 'today', label: 'Today' },
            { value: 'week', label: 'This Week' },
            { value: 'month', label: 'This Month' },
            { value: 'year', label: 'This Year' },
            { value: 'overdue', label: 'Overdue' },
          ]}
          value={dateFilter}
          onChange={(value) => setDateFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-financial-filter-dropdown"
        />
      </div>

      <section className="stakeholder-section-block">
        <h4>{activeConfig.title}</h4>
        {activeConfig.loading ? (
          <div>Loading...</div>
        ) : (
          <Table
            columns={activeConfig.columns}
            data={activeConfig.data}
            rowKey={activeConfig.rowKey}
            emptyMessage={activeConfig.empty}
          />
        )}
      </section>
    </div>
  );
};

export default StakeholderFinancials;

